import React, { useEffect, useRef, useState } from 'react'
import { Button, Modal, ProgressBar } from '@enamelai/design-system'
import {
  getFileIconClass,
  getFileTypeFromFileName,
} from '@components/pages/Files/filesAndLetters.util'
import './FilesUploadModal.css'

export type FileUpload = {
  name: string
  size: number
  progress: number
  uploaded: boolean
  lastUpdated: string
  /** Raw file for creating preview URLs (e.g. blob URL) in the parent */
  file?: File
}

type FilesUploadModalProps = {
  show: boolean
  onHide: () => void
  onAttach?: (files: FileUpload[]) => void
}

const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg', 'dcm']
const MAX_FILE_SIZE = 20 * 1024 * 1024
const PROGRESS_STEP = 10
const PROGRESS_INTERVAL_MS = 200

const FilesUploadModal: React.FC<FilesUploadModalProps> = ({
  show,
  onHide,
  onAttach,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<FileUpload[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const intervalsRef = useRef<Map<string, ReturnType<typeof setInterval>>>(
    new Map(),
  )

  const clearProgressTimer = (name: string): void => {
    const timer = intervalsRef.current.get(name)
    if (timer) clearInterval(timer)
    intervalsRef.current.delete(name)
  }

  useEffect(() => {
    if (show) return
    intervalsRef.current.forEach((timer) => clearInterval(timer))
    intervalsRef.current.clear()
    setUploadedFiles([])
    setErrorMessage(null)
    setIsDragging(false)
  }, [show])

  useEffect(() => {
    const intervals = intervalsRef.current
    return () => intervals.forEach((timer) => clearInterval(timer))
  }, [])

  const startProgress = (name: string): void => {
    let progress = 0
    const timer = setInterval(() => {
      progress += PROGRESS_STEP
      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.name === name ? { ...f, progress, uploaded: progress >= 100 } : f,
        ),
      )
      if (progress >= 100) clearProgressTimer(name)
    }, PROGRESS_INTERVAL_MS)
    intervalsRef.current.set(name, timer)
  }

  const addFiles = (files: File[]): void => {
    let error: string | null = null
    const existingNames = new Set(uploadedFiles.map((f) => f.name))
    const accepted = files.filter((file) => {
      const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
      if (!ALLOWED_EXTENSIONS.includes(extension)) {
        error = 'Unsupported file type'
        return false
      }
      if (file.size > MAX_FILE_SIZE) {
        error = 'File size exceeds 20 MB limit'
        return false
      }
      if (existingNames.has(file.name)) {
        error = 'File is already uploaded'
        return false
      }
      existingNames.add(file.name)
      return true
    })

    setErrorMessage(accepted.length > 0 ? null : error)
    if (accepted.length === 0) return

    setUploadedFiles((prev) => [
      ...prev,
      ...accepted.map((file) => ({
        name: file.name,
        size: file.size,
        progress: 0,
        uploaded: false,
        lastUpdated: new Date().toLocaleDateString(),
        file,
      })),
    ])
    accepted.forEach((file) => startProgress(file.name))
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    addFiles(Array.from(e.target.files ?? []))
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    addFiles(Array.from(e.dataTransfer.files))
  }

  const handleRemove = (name: string): void => {
    clearProgressTimer(name)
    setUploadedFiles((prev) => prev.filter((f) => f.name !== name))
  }

  const handleAttach = (): void => {
    const ready = uploadedFiles.filter((f) => f.uploaded)
    if (ready.length === 0) {
      setErrorMessage('Please wait for files to finish uploading')
      return
    }
    onAttach?.(ready)
    onHide()
  }

  const openFilePicker = (): void => inputRef.current?.click()

  return (
    <Modal
      open={show}
      onClose={onHide}
      size="sm"
      title="Upload files"
      description="Upload and attach files to this patient record"
      className="files-upload-modal"
      bodyClassName="files-upload-modal-body"
      secondaryLabel="Cancel"
      onSecondaryClick={onHide}
      primaryLabel="Attach files"
      primaryDisabled={!uploadedFiles.some((f) => f.uploaded)}
      onPrimaryClick={handleAttach}
    >
      <div
        className={`files-upload-dropzone${isDragging ? ' files-upload-dropzone--dragging' : ''}`}
        role="button"
        tabIndex={0}
        onClick={openFilePicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            openFilePicker()
          }
        }}
        onDragOver={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setIsDragging(true)
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setIsDragging(false)
        }}
        onDrop={handleDrop}
      >
        <i
          className="mgc mgc_upload_2_line files-upload-dropzone-icon"
          aria-hidden
        />
        <p className="files-upload-dropzone-text">
          <span className="files-upload-dropzone-link">Click to upload</span> or
          drag and drop
        </p>
        <p className="files-upload-dropzone-hint">
          PDF, DOC, PNG, JPG, DCM (max. 20MB)
        </p>
        <input
          ref={inputRef}
          type="file"
          className="files-upload-input"
          accept={ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',')}
          onChange={handleInputChange}
          multiple
        />
      </div>

      {errorMessage && (
        <p className="files-upload-error" role="alert">
          {errorMessage}
        </p>
      )}

      {uploadedFiles.length > 0 && (
        <ul className="files-upload-list">
          {uploadedFiles.map((file) => {
            const fileType = getFileTypeFromFileName(file.name)
            return (
              <li key={file.name} className="files-upload-item">
                <i
                  className={`mgc ${getFileIconClass(fileType)} files-upload-item-icon files-upload-item-icon--${fileType}`}
                  aria-hidden
                />
                <div className="files-upload-item-details">
                  <div className="files-upload-item-header">
                    <span className="files-upload-item-name">{file.name}</span>
                    <span className="files-upload-item-size">
                      {(file.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                  <ProgressBar
                    value={file.progress}
                    aria-label={`${file.name} upload progress`}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="xs"
                  className="files-upload-item-remove"
                  aria-label={`Remove ${file.name}`}
                  onClick={() => handleRemove(file.name)}
                >
                  <i
                    className={`mgc ${file.uploaded ? 'mgc_delete_2_line' : 'mgc_close_line'}`}
                    aria-hidden
                  />
                </Button>
              </li>
            )
          })}
        </ul>
      )}
    </Modal>
  )
}

export default FilesUploadModal
